import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FeedbackModal, Icon } from "@/components";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import "./styles.scss";
import DashboardLayout from "@/layouts/dashboard-layout";
import truncateString from "@/utils/truncateString";
import { useAuth } from "@/contexts/auth-context";
import { formatGameTimeStamp, formatTimeStamp } from "@/utils/function";
import { completionFn } from "@/redux/slice/activity/completion";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useCompletionContext } from "@/contexts/PointsContext";
import { Activity } from "@/types/activity";
import * as htmlToImage from "html-to-image";
import { GenerateImage } from "./GenerateImage";

export const Complete = () => {
  const { lesson_id } = useParams();
  const dispatch = useAppDispatch();
  const { data, error, loading } = useAppSelector((state) => state.completion);
  const { data: activity } = useAppSelector((state) => state.activities);
  const viewRef = useRef<HTMLDivElement>(null);

  const [isGeneratingImage, setIsGeneratingImage] = useState(true);
  const [isSharing, setIsSharing] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(true);
  const [shareableFile, setShareableFile] = useState<File | null>(null);
  const [shareableDataUrl, setShareableDataUrl] = useState<string>("");

  const {
    data: activityLessons,
    error: activityErrors,
    loading: activityLoading,
  } = useAppSelector((state) => state.activityLesson);

  const completedItem =
    activityLessons &&
    activityLessons.find((a: Activity) => a.id === lesson_id);

  const { points, setPoints } = useCompletionContext();

  useEffect(() => {
    if (!points || points?.length === 0) {
      setPoints(completedItem?.points);
    }
  }, [activityLessons]);

  const navigate = useNavigate();
  const { trialCount, gameTimeStamp, activityValue, token, details } =
    useAuth();

  const numberOfQuestion = activityLoading
    ? 1
    : activity && activity.length > 0
      ? activity[0].activityType === "ChatActivity"
        ? activity[0].series?.length
        : activity.length
      : 1;

  const accuracy = activity
    ? activity[0].activityType === "ChatActivity"
      ? numberOfQuestion / (trialCount + numberOfQuestion)
      : numberOfQuestion / trialCount
    : 0;

  const fetch = useCallback(() => {
    if (details) {
      dispatch(
        completionFn({
          token: token!,
          activityValue,
          time: formatTimeStamp(
            (gameTimeStamp?.end - gameTimeStamp?.start) / 1000,
          ),
          id: details?.id,
          accuracy: accuracy,
        }),
      );
    }
  }, [
    accuracy,
    activityValue,
    details,
    dispatch,
    gameTimeStamp?.end,
    gameTimeStamp?.start,
    token,
  ]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    const generateShareImage = async () => {
      if (!viewRef.current || !data?.data?.result?.submitted?.language) return;

      setIsGeneratingImage(true);
      try {
        const dataUrl = await htmlToImage.toPng(viewRef.current!, {
          cacheBust: true,
        });

        const byteString = atob(dataUrl.split(",")[1]);
        const mimeString = dataUrl.split(",")[0].split(":")[1].split(";")[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });
        const file = new File([blob], "my-result.png", { type: "image/png" });

        setShareableFile(file);
        setShareableDataUrl(dataUrl);
      } catch (err) {
        console.error("Failed to generate share image", err);
      } finally {
        setIsGeneratingImage(false);
      }
    };

    setTimeout(generateShareImage, 500);
  }, [accuracy, points, data?.data?.result?.submitted?.language]);

  const handleShare = async () => {
    if (!shareableFile) {
      alert("Please wait, preparing image...");
      return;
    }

    setIsSharing(true);
    try {
      if (navigator.share && navigator.canShare({ files: [shareableFile] })) {
        await navigator.share({
          title: "My Pluritongues Language Test Result",
          text: `${data?.data?.result?.sharing?.url}`,
          files: [shareableFile],
        });
      } else {
        await navigator.share({
          title: "My Test Result",
          text: `I just scored ${(accuracy * 100).toFixed(2)}%!`,
        });
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error("Share failed", err);
      }
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <DashboardLayout>
      {loading ? (
        <p>Loading</p>
      ) : (
        <FeedbackModal
          visible={showModal}
          onClose={() => {
            setShowModal(false);
          }}
          userScore={Math.round(accuracy * 100)}
          passScore={97}
          showModal={showModal}
          setShowModal={setShowModal}
        />
      )}

      <div className="completion">
        <div className="completion__scroll-view">
          <h1 className="completion__title">Lesson Completed</h1>

          <div className="completion__progress">
            <div className="completion__progress-circle">
              <CircularProgressbar
                value={accuracy * 100}
                text={``}
                styles={buildStyles({
                  textColor: "#127FBE",
                  pathColor: "#127FBE",
                  trailColor: "#E1E8ED",
                  textSize: "16px",
                  strokeLinecap: "round",
                })}
                counterClockwise={true}
              />
              <p className="completion__accuracy">
                Accuracy <br /> {(accuracy * 100).toFixed(0)}%
              </p>
            </div>
            <div className="completion__details">
              <p className="completion__details-text">Completion Time</p>
              <p className="completion__details-time">
                {formatGameTimeStamp(
                  (gameTimeStamp?.end - gameTimeStamp?.start) / 1000,
                )}
              </p>
            </div>
          </div>

          <h2 className="completion__sub-title">Your Learning Progress</h2>

          {points?.map((item, index) => (
            <div className="completion__learnt" key={index}>
              <Icon name="checkBlue" />
              <p className="completion__learnt-text">
                {truncateString(item, 60)}
              </p>
            </div>
          ))}
        </div>

        <div className="completion__modal">
          <p
            style={{ cursor: isGeneratingImage ? "not-allowed" : "pointer" }}
            onClick={!isGeneratingImage ? () => handleShare() : undefined}
          >
            <Icon name={isGeneratingImage || isSharing ? "spin" : "share"} />
          </p>

          <button
            className="completion__modal-btn"
            onClick={() => navigate("/activity")}
            disabled={isGeneratingImage || isSharing}
          >
            Continue
          </button>
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          top: -9999,
          left: -9999,
        }}
      >
        <GenerateImage viewRef={viewRef} accuracy={accuracy} data={data} />
      </div>
    </DashboardLayout>
  );
};
